// Query Key Constants and Functions
// Constants are for cache invalidation, functions are for query key generation with parameters

import { TutorType } from "@/types/enums";

// Auth & Profile
export const USER_ROLE_QUERY_KEY = 'user-role';
export const getUserRoleQueryKey = (userId: string | null | undefined) => [USER_ROLE_QUERY_KEY, userId];

export const STAFF_PROFILE_QUERY_KEY = 'staff-profile';
export const getStaffProfileQueryKey = (userId: string | null | undefined) => [STAFF_PROFILE_QUERY_KEY, userId];

export const TUTOR_PROFILE_QUERY_KEY = 'tutor-profile';
export const getTutorProfileQueryKey = (userId: string | null | undefined) => [TUTOR_PROFILE_QUERY_KEY, userId];

export const CLIENT_PROFILE_QUERY_KEY = 'client-profile';
export const getClientProfileQueryKey = (userId: string | null | undefined) => [CLIENT_PROFILE_QUERY_KEY, userId];

export const CURRENT_PROFILE_QUERY_KEY = 'current-profile';
export const getCurrentProfileQueryKey = (userId: string | null | undefined) => [CURRENT_PROFILE_QUERY_KEY, userId];

// Jobs
export const JOBS_QUERY_KEY = 'jobs';
export const getJobsQueryKey = (
  searchQuery: string,
  jobStageFilter: string,
  matchingStageFilter: string,
  page: number,
  pageSize: number,
  showUrgentOnly: boolean,
  selectedClientManagerUserId: string
) => [JOBS_QUERY_KEY, searchQuery, jobStageFilter, matchingStageFilter, page, pageSize, showUrgentOnly, selectedClientManagerUserId];

export const JOB_QUERY_KEY = 'job';
export const getJobQueryKey = (jobId: number) => [JOB_QUERY_KEY, jobId];

export const JOB_DETAILS_QUERY_KEY = 'job-details';
export const getJobDetailsQueryKey = (jobId: number) => [JOB_DETAILS_QUERY_KEY, jobId];

export const JOB_DETAILS_EXTERNAL_QUERY_KEY = 'job-details-external';
export const getJobDetailsExternalQueryKey = (externalId: string | null | undefined) => [JOB_DETAILS_EXTERNAL_QUERY_KEY, externalId];

export const TUTOR_JOB_DETAILS_QUERY_KEY = 'tutor-job-details';
export const getTutorJobDetailsQueryKey = (userId: string | null | undefined, jobId: number) => [TUTOR_JOB_DETAILS_QUERY_KEY, userId, jobId];

export const ACTIVE_JOB_DETAILS_QUERY_KEY = 'active-job-details';
export const getActiveJobDetailsQueryKey = (id: string | null | undefined) => [ACTIVE_JOB_DETAILS_QUERY_KEY, id];

export const JOBS_BOARD_QUERY_KEY = 'jobs-board';
export const getJobsBoardQueryKey = (
  location: string,
  includeDismissed: boolean,
  days: string[],
  page: number,
  pageSize: number
) => [JOBS_BOARD_QUERY_KEY, location, includeDismissed, days.join(','), page, pageSize];

// Job Applications
export const APPLICATIONS_QUERY_KEY = 'applications';
export const getApplicationsQueryKey = (userId: string | null | undefined) => [APPLICATIONS_QUERY_KEY, userId];

export const TUTOR_JOB_APPLICATION_QUERY_KEY = 'tutor-job-application';
export const getTutorJobApplicationQueryKey = (jobId: number) => [TUTOR_JOB_APPLICATION_QUERY_KEY, jobId];

// Job Lessons & Reports
export const TUTOR_JOB_LESSONS_QUERY_KEY = 'tutor-job-lessons';
export const getTutorJobLessonsQueryKey = (jobId: number) => [TUTOR_JOB_LESSONS_QUERY_KEY, jobId];

export const TUTOR_JOB_REPORTS_QUERY_KEY = 'tutor-job-reports';
export const getTutorJobReportsQueryKey = (jobId: number) => [TUTOR_JOB_REPORTS_QUERY_KEY, jobId];

// Staff
export const STAFF_SCHOOLS_QUERY_KEY = 'staff-schools';
export const getStaffSchoolsQueryKey = () => [STAFF_SCHOOLS_QUERY_KEY];

export const STAFF_CLIENTS_QUERY_KEY = 'staff-clients';
export const getStaffClientsQueryKey = () => [STAFF_CLIENTS_QUERY_KEY];

export const STAFF_STUDENTS_QUERY_KEY = 'staff-students';
export const getStaffStudentsQueryKey = (contactId: number | null | undefined) => [STAFF_STUDENTS_QUERY_KEY, contactId];

export const STAFF_TUTORS_QUERY_KEY = 'staff-tutors';
export const getStaffTutorsQueryKey = (searchQuery: string, page: number, pageSize: number) =>
  [STAFF_TUTORS_QUERY_KEY, searchQuery, page, pageSize];

export const STAFF_TUTORS_OVERTIME_LIST_QUERY_KEY = 'staff-tutors-overtime-list';
export const getStaffTutorsOvertimeListQueryKey = () => [STAFF_TUTORS_OVERTIME_LIST_QUERY_KEY];

export const STAFF_REPORTS_QUERY_KEY = 'staff-reports';
export const getStaffReportsQueryKey = (
  searchQuery: string,
  tutorId: string | null | undefined,
  isReady: boolean,
  page: number,
  pageSize: number,
  approvalFilter: string
) => [STAFF_REPORTS_QUERY_KEY, searchQuery, tutorId, isReady, page, pageSize, approvalFilter];

export const STAFF_CLIENT_REPORTS_QUERY_KEY = 'staff-client-reports';
export const getStaffClientReportsQueryKey = (
  clientId: string | null,
  clientManagerId: string | null,
  isSent: boolean,
  page: number,
  pageSize: number
) => [STAFF_CLIENT_REPORTS_QUERY_KEY, clientId, clientManagerId, isSent, page, pageSize];

export const STAFF_PAYMENTS_QUERY_KEY = 'staff-payments';
export const getStaffPaymentsQueryKey = (
  paymentStatus: string,
  searchQuery: string,
  tutorType: string,
  endDate: string,
  page: number,
  pageSize: number
) => [STAFF_PAYMENTS_QUERY_KEY, paymentStatus, searchQuery, tutorType, endDate, page, pageSize];

export const STAFF_OVERTIME_PAYMENTS_QUERY_KEY = 'staff-overtime-payments';
export const getStaffOvertimePaymentsQueryKey = (
  tutorId: string | null | undefined,
  page: number,
  pageSize: number
) => [STAFF_OVERTIME_PAYMENTS_QUERY_KEY, tutorId, page, pageSize];

export const STAFF_TUTOR_PAYMENTS_SUMMARY_QUERY_KEY = 'staff-tutor-payments-summary';
export const getStaffTutorPaymentsSummaryQueryKey = (
  tutorId: string | null | undefined,
  page: number,
  pageSize: number
) => [STAFF_TUTOR_PAYMENTS_SUMMARY_QUERY_KEY, tutorId, page, pageSize];

// Tutor
export const TUTOR_JOBS_QUERY_KEY = 'tutor-jobs';
export const getTutorJobsQueryKey = (userId: string | null | undefined) => [TUTOR_JOBS_QUERY_KEY, userId];

export const TUTOR_LESSONS_QUERY_KEY = 'tutor-lessons';
export const getTutorLessonsQueryKey = (
  statusFilter: string,
  page: number,
  searchQuery: string,
  dateFrom: string,
  dateTo: string
) => [TUTOR_LESSONS_QUERY_KEY, statusFilter, page, searchQuery, dateFrom, dateTo];

export const TUTOR_EXPENSES_QUERY_KEY = 'tutor-expenses';
export const getTutorExpensesQueryKey = (
  userId: string | null | undefined,
  statusFilter: string,
  page: number,
  searchQuery: string,
  dateFrom: string,
  dateTo: string,
  pageSize: number
) => [TUTOR_EXPENSES_QUERY_KEY, userId, statusFilter, page, searchQuery, dateFrom, dateTo, pageSize];

export const TUTOR_PAYMENTS_SUMMARY_QUERY_KEY = 'tutor-payments-summary';
export const getTutorPaymentsSummaryQueryKey = (userId: string | null | undefined) => [TUTOR_PAYMENTS_SUMMARY_QUERY_KEY, userId];

export const TUTOR_REPORTS_QUERY_KEY = 'tutor-reports';
export const getTutorReportsQueryKey = (userId: string | undefined) => [TUTOR_REPORTS_QUERY_KEY, userId];

// Client
export const CLIENT_REPORTS_QUERY_KEY = 'client-reports';
export const getClientReportsQueryKey = () => [CLIENT_REPORTS_QUERY_KEY];

export const CLIENT_MANAGERS_QUERY_KEY = 'client-managers';
export const getClientManagersQueryKey = () => [CLIENT_MANAGERS_QUERY_KEY];

export const CONTACTS_QUERY_KEY = 'contacts';
export const getContactsQueryKey = () => [CONTACTS_QUERY_KEY];

// Finance & Billing
export const INVOICES_QUERY_KEY = 'invoices';
export const getInvoicesQueryKey = (
  paymentStatus: string,
  billingType: string | null | undefined,
  searchQuery: string,
  page: number,
  pageSize: number
) => [INVOICES_QUERY_KEY, paymentStatus, billingType, searchQuery, page, pageSize];

export const LINE_ITEMS_QUERY_KEY = 'line-items';
export const getLineItemsQueryKey = (invoiceId: string | null) => [LINE_ITEMS_QUERY_KEY, invoiceId];

export const PENDING_BATCH_IDS_QUERY_KEY = 'pending-batch-ids';
export const getPendingBatchIdsQueryKey = (tutorType: TutorType) => [PENDING_BATCH_IDS_QUERY_KEY, tutorType];

export const PAYMENT_METHODS_QUERY_KEY = 'payment-methods';
export const getPaymentMethodsQueryKey = (jobId: number) => [PAYMENT_METHODS_QUERY_KEY, jobId];

export const GET_PAYMENT_METHODS_QUERY_KEY = 'get-payment-methods';
export const getGetPaymentMethodsQueryKey = () => [GET_PAYMENT_METHODS_QUERY_KEY];

export const GET_ACCOUNT_BALANCE_QUERY_KEY = 'get-account-balance';
export const getGetAccountBalanceQueryKey = () => [GET_ACCOUNT_BALANCE_QUERY_KEY];

export const BANK_DETAILS_QUERY_KEY = 'bank-details';
export const getBankDetailsQueryKey = () => [BANK_DETAILS_QUERY_KEY];

// Resources & Utilities
export const RESOURCES_QUERY_KEY = 'resources';
export const getResourcesQueryKey = () => [RESOURCES_QUERY_KEY];

export const EXCHANGE_RATES_QUERY_KEY = 'exchange-rates';
export const getExchangeRatesQueryKey = () => [EXCHANGE_RATES_QUERY_KEY];

export const EMAIL_TEMPLATE_QUERY_KEY = 'email-template';
export const getEmailTemplateQueryKey = (
  matchingStage: string,
  jobId: number,
  jobKey: string
) => [EMAIL_TEMPLATE_QUERY_KEY, matchingStage, jobId, jobKey];

export const ITEMS_QUERY_KEY = 'items';
export const getItemsQueryKey = (page: number, searchQuery: string) => [ITEMS_QUERY_KEY, page, searchQuery];

// Reports
export const REPORT_QUERY_KEY = 'report';
export const getReportQueryKey = (id: string | undefined, userId: string | null | undefined) => [REPORT_QUERY_KEY, id, userId];

// Database Views
export const JOBS_VIEW_QUERY_KEY = 'jobs-view';
export const getJobsViewQueryKey = (jobId: number | null | undefined) => [JOBS_VIEW_QUERY_KEY, jobId];

// Tutors (general)
export const TUTORS_QUERY_KEY = 'tutors';
export const getTutorsQueryKey = () => [TUTORS_QUERY_KEY];

export const AVAILABLE_TUTORS_QUERY_KEY = 'available-tutors';
export const getAvailableTutorsQueryKey = (
  searchQuery: string,
  minRate: number,
  maxRate: number,
  selectedSubjectIds: string,
  minMatchedSkills: number,
  tutorType: string | null
) => [AVAILABLE_TUTORS_QUERY_KEY, searchQuery, minRate, maxRate, selectedSubjectIds, minMatchedSkills, tutorType];
